import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Button, Badge, Card, Alert, Image,
} from 'react-bootstrap';
import _ from 'lodash';
import { post } from '../../../Services/API';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import AddressAutocomplete from '../../../Components/AddressAutocomplete';
import ExperienceService from '../../../Services/ExperienceService';
import ExperienceGroupService from '../../../Services/ExperienceGroupService';
import PricingService from '../../../Services/PricingService';

const STOP_TYPES = [
  { value: 'break', label: 'Break' },
  { value: 'activity', label: 'Activity' },
];

const CURRENCIES = ['EUR', 'HUF'];

const PRICING_TYPES = [
  { value: 'per_group', label: 'Per Group (flat rate)' },
  { value: 'per_person', label: 'Per Person (multiply by adults)' },
  { value: 'passenger_tiers', label: 'Passenger Tiers (tiered by group size)' },
  { value: 'vehicle_category', label: 'Vehicle Category (for transfers)' },
];

function ExperienceInputModal({
  originalExperience = {},
  show = false,
  handleClose,
  fetchExperiences,
}) {
  const defaultExperience = {
    active: 1,
    name: '',
    description: '',
    duration_hours: 0,
    itinerary_template: [],
    default_pricing: {
      price: 0,
      child_price: 0,
      currency: 'EUR',
      pricing_type: 'per_group',
      passenger_tiers: [],
      vehicle_category_prices: [],
    },
    starting_point: {
      address: '',
      latitude: null,
      longitude: null,
    },
    ending_point: {
      address: '',
      latitude: null,
      longitude: null,
    },
    // Website display fields
    link: '',
    // Group association
    group_id: '',
    sort_order: 0,
    // Important information and cancellation policy
    important_information: '',
    cancellation_policy: '',
    // Public booking fields
    max_passengers: 8,
    blocked_dates: [],
    add_ons: [],
  };

  const [experience, setExperience] = useState(
    _.defaults({ ...originalExperience }, defaultExperience),
  );
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [experienceGroups, setExperienceGroups] = useState([]);
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch experience groups and vehicle categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsResponse, categoriesResponse] = await Promise.all([
          ExperienceGroupService.fetchExperienceGroups(),
          PricingService.fetchVehicleCategories(),
        ]);
        setExperienceGroups(groupsResponse.data?.data || []);
        setVehicleCategories(categoriesResponse.data?.data || []);
      } catch (err) {
        setExperienceGroups([]);
        setVehicleCategories([]);
      }
    };
    if (show) {
      fetchData();
    }
  }, [show]);

  // Handle image file selection
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PNG, JPEG, and WebP images are allowed');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/xxx;base64, prefix

      // If experience already exists, upload immediately
      if (experience.Id) {
        setImageUploading(true);
        try {
          const result = await ExperienceService.uploadExperienceImage(
            experience.Id,
            base64,
            file.type,
          );
          setExperience((prev) => ({
            ...prev,
            image_key: result.data?.data?.image_key,
            image_url: result.data?.data?.image_url,
          }));
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to upload image');
        } finally {
          setImageUploading(false);
        }
      } else {
        // For new experiences, store the pending image data
        setExperience((prev) => ({
          ...prev,
          pendingImage: { base64, contentType: file.type },
          imagePreview: reader.result,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove image
  const handleRemoveImage = async () => {
    if (experience.Id && experience.image_key) {
      setImageUploading(true);
      try {
        await ExperienceService.removeExperienceImage(experience.Id);
        setExperience((prev) => ({
          ...prev,
          image_key: undefined,
          image_url: undefined,
        }));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to remove image');
      } finally {
        setImageUploading(false);
      }
    } else {
      // Clear pending image
      setExperience((prev) => ({
        ...prev,
        pendingImage: undefined,
        imagePreview: undefined,
      }));
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Build payload, only include starting_point/ending_point if they have an address
      const payload = {
        ...experience,
        active: Number(experience.active),
        sort_order: Number(experience.sort_order) || 0,
      };

      // Only include starting_point if it has an address
      if (experience.starting_point?.address) {
        payload.starting_point = {
          address: experience.starting_point.address,
          latitude: experience.starting_point.latitude,
          longitude: experience.starting_point.longitude,
        };
      } else {
        delete payload.starting_point;
      }

      // Only include ending_point if it has an address
      if (experience.ending_point?.address) {
        payload.ending_point = {
          address: experience.ending_point.address,
          latitude: experience.ending_point.latitude,
          longitude: experience.ending_point.longitude,
        };
      } else {
        delete payload.ending_point;
      }

      // Remove UI-only fields
      delete payload.pendingImage;
      delete payload.imagePreview;
      delete payload.image_url;

      const result = await post('/experiences', payload);
      const savedExperience = result.data?.data;

      // If this was a new experience and we have a pending image, upload it now
      if (!experience.Id && experience.pendingImage && savedExperience?.Id) {
        try {
          await ExperienceService.uploadExperienceImage(
            savedExperience.Id,
            experience.pendingImage.base64,
            experience.pendingImage.contentType,
          );
        } catch (imgErr) {
          // Log but don't fail - experience was created successfully
          // eslint-disable-next-line no-console
          console.error('Failed to upload image:', imgErr);
        }
      }

      fetchExperiences();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save experience');

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setStateFromInput(e, setExperience, experience);
  };

  const addItineraryActivity = () => {
    const newActivity = {
      order: (experience.itinerary_template?.length || 0) + 1,
      description: '',
      address: '',
      stop_type: 'activity',
      duration_minutes: 30,
    };
    setExperience((prev) => ({
      ...prev,
      itinerary_template: [...(prev.itinerary_template || []), newActivity],
    }));
  };

  const removeItineraryActivity = (index) => {
    const activities = [...(experience.itinerary_template || [])];
    activities.splice(index, 1);
    // Re-order
    const reordered = activities.map((act, i) => ({ ...act, order: i + 1 }));
    setExperience((prev) => ({ ...prev, itinerary_template: reordered }));
  };

  const updateItineraryActivity = (index, field, value) => {
    setExperience((prev) => {
      const activities = [...(prev.itinerary_template || [])];
      activities[index] = { ...activities[index], [field]: value };
      return { ...prev, itinerary_template: activities };
    });
  };

  // Passenger tier helpers
  const addPassengerTier = () => {
    const tiers = experience.default_pricing?.passenger_tiers || [];
    const lastTier = tiers[tiers.length - 1];
    const newTier = {
      min_passengers: lastTier ? lastTier.max_passengers + 1 : 1,
      max_passengers: lastTier ? lastTier.max_passengers + 3 : 3,
      price_eur: 0,
      price_huf: 0,
    };
    setExperience((prev) => ({
      ...prev,
      default_pricing: {
        ...prev.default_pricing,
        passenger_tiers: [...(prev.default_pricing?.passenger_tiers || []), newTier],
      },
    }));
  };

  const removePassengerTier = (index) => {
    setExperience((prev) => {
      const tiers = [...(prev.default_pricing?.passenger_tiers || [])];
      tiers.splice(index, 1);
      return {
        ...prev,
        default_pricing: { ...prev.default_pricing, passenger_tiers: tiers },
      };
    });
  };

  const updatePassengerTier = (index, field, value) => {
    setExperience((prev) => {
      const tiers = [...(prev.default_pricing?.passenger_tiers || [])];
      tiers[index] = { ...tiers[index], [field]: value };
      return {
        ...prev,
        default_pricing: { ...prev.default_pricing, passenger_tiers: tiers },
      };
    });
  };

  // Vehicle category price helpers
  const addVehicleCategoryPrice = (categoryId) => {
    const category = vehicleCategories.find((c) => c.Id === categoryId);
    if (!category) return;

    // Check if already added
    const existing = experience.default_pricing?.vehicle_category_prices || [];
    if (existing.some((p) => p.vehicle_category_id === categoryId)) return;

    const newPrice = {
      vehicle_category_id: categoryId,
      price_eur: 0,
      price_huf: 0,
    };
    setExperience((prev) => ({
      ...prev,
      default_pricing: {
        ...prev.default_pricing,
        vehicle_category_prices: [...existing, newPrice],
      },
    }));
  };

  const removeVehicleCategoryPrice = (index) => {
    setExperience((prev) => {
      const prices = [...(prev.default_pricing?.vehicle_category_prices || [])];
      prices.splice(index, 1);
      return {
        ...prev,
        default_pricing: { ...prev.default_pricing, vehicle_category_prices: prices },
      };
    });
  };

  const updateVehicleCategoryPrice = (index, field, value) => {
    setExperience((prev) => {
      const prices = [...(prev.default_pricing?.vehicle_category_prices || [])];
      prices[index] = { ...prices[index], [field]: value };
      return {
        ...prev,
        default_pricing: { ...prev.default_pricing, vehicle_category_prices: prices },
      };
    });
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = vehicleCategories.find((c) => c.Id === categoryId);
    return category?.name || 'Unknown';
  };

  // Get available categories (not yet added)
  const getAvailableCategories = () => {
    const addedIds = (experience.default_pricing?.vehicle_category_prices || [])
      .map((p) => p.vehicle_category_id);
    return vehicleCategories.filter((c) => !addedIds.includes(c.Id) && c.active === 1);
  };

  // ── Blocked dates helpers ──────────────────────────────────────────────────
  const [newBlockedDate, setNewBlockedDate] = useState('');

  const addBlockedDate = () => {
    const date = newBlockedDate.trim();
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    if ((experience.blocked_dates || []).includes(date)) return;
    setExperience((prev) => ({
      ...prev,
      blocked_dates: [...(prev.blocked_dates || []), date].sort(),
    }));
    setNewBlockedDate('');
  };

  const removeBlockedDate = (date) => {
    setExperience((prev) => ({
      ...prev,
      blocked_dates: (prev.blocked_dates || []).filter((d) => d !== date),
    }));
  };

  // ── Add-ons helpers ────────────────────────────────────────────────────────
  const addAddOn = () => {
    const newAddOn = {
      id: `addon-${Date.now()}`,
      name: '',
      description: '',
      price_eur: 0,
      price_huf: 0,
      price_type: 'per_person',
    };
    setExperience((prev) => ({
      ...prev,
      add_ons: [...(prev.add_ons || []), newAddOn],
    }));
  };

  const removeAddOn = (index) => {
    setExperience((prev) => {
      const addOns = [...(prev.add_ons || [])];
      addOns.splice(index, 1);
      return { ...prev, add_ons: addOns };
    });
  };

  const updateAddOn = (index, field, value) => {
    setExperience((prev) => {
      const addOns = [...(prev.add_ons || [])];
      addOns[index] = { ...addOns[index], [field]: value };
      return { ...prev, add_ons: addOns };
    });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {experience.Id ? (
            <>
              Edit Experience:
              {' '}
              {experience.name}
            </>
          ) : (
            <>New Experience</>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Form>
          {/* Basic Information */}
          <h6 className="border-bottom pb-2 mb-3">
            <i className="fa fa-info-circle me-2" />
            Basic Information
          </h6>

          <Form.Group className="mb-3" controlId="name">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={experience.name}
              onChange={handleChange}
              placeholder="e.g., Budapest City Walking Tour"
            />
            <FormControlFeedback errors={validationErrors} field="name" />
          </Form.Group>

          <Form.Group className="mb-3" controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={experience.description}
              onChange={handleChange}
              placeholder="Describe the experience..."
            />
            <FormControlFeedback errors={validationErrors} field="description" />
          </Form.Group>

          <Form.Group className="mb-3" controlId="important_information">
            <Form.Label>Important Information (shown on voucher)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={experience.important_information || ''}
              onChange={handleChange}
              placeholder="Enter important information for customers (e.g., what to bring, dress code, meeting point details). Line breaks will be preserved on the voucher."
            />
            <Form.Text className="text-muted">
              This will be copied to bookings and displayed on the voucher. Supports line breaks.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="cancellation_policy">
            <Form.Label>Cancellation Policy</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={experience.cancellation_policy || ''}
              onChange={handleChange}
              placeholder="e.g., Free cancellation up to 48 hours before the experience"
            />
            <Form.Text className="text-muted">
              If not set, bookings will default to &quot;24 hour cancellation policy&quot;
            </Form.Text>
          </Form.Group>

          <div className="row">
            <Form.Group className="mb-3 col-4" controlId="duration_hours">
              <Form.Label>Duration (hours) *</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step={0.1}
                value={experience.duration_hours}
                onChange={handleChange}
              />
              <FormControlFeedback errors={validationErrors} field="duration_hours" />
            </Form.Group>

            <Form.Group className="mb-3 col-4" controlId="default_pricing.pricing_type">
              <Form.Label>Pricing Type</Form.Label>
              <Form.Select
                value={experience.default_pricing?.pricing_type || 'per_group'}
                onChange={handleChange}
              >
                {PRICING_TYPES.map((pt) => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </Form.Select>
              <FormControlFeedback errors={validationErrors} field="default_pricing.pricing_type" />
            </Form.Group>
          </div>

          {/* Simple pricing (per_group or per_person) */}
          {['per_group', 'per_person'].includes(
            experience.default_pricing?.pricing_type,
          ) && (
            <div className="row">
              <Form.Group className="mb-3 col-3" controlId="default_pricing.price">
                <Form.Label>
                  {experience.default_pricing?.pricing_type === 'per_person'
                    ? 'Price per Adult'
                    : 'Group Price'}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={experience.default_pricing?.price || 0}
                  onChange={handleChange}
                />
                <FormControlFeedback errors={validationErrors} field="default_pricing.price" />
              </Form.Group>

              {experience.default_pricing?.pricing_type === 'per_person' && (
                <Form.Group className="mb-3 col-3" controlId="default_pricing.child_price">
                  <Form.Label>Price per Child (0-12)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={experience.default_pricing?.child_price || 0}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="default_pricing.child_price" />
                </Form.Group>
              )}

              <Form.Group className="mb-3 col-3" controlId="default_pricing.currency">
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={experience.default_pricing?.currency || 'EUR'}
                  onChange={handleChange}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {/* Passenger Tiers pricing */}
          {experience.default_pricing?.pricing_type === 'passenger_tiers' && (
            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fa fa-users me-2" />
                    Passenger Tiers
                  </span>
                  <Button size="sm" variant="outline-primary" onClick={addPassengerTier}>
                    <i className="fa fa-plus me-1" />
                    Add Tier
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="p-2">
                {(!experience.default_pricing?.passenger_tiers
                  || experience.default_pricing.passenger_tiers.length === 0) && (
                  <div className="text-muted text-center py-3">
                    <i className="fa fa-info-circle me-2" />
                    No tiers defined. Add tiers for different group sizes.
                  </div>
                )}
                {experience.default_pricing?.passenger_tiers?.map((tier, index) => {
                  const tierKey = `tier-${index}`;
                  return (
                    <div key={tierKey} className="d-flex gap-2 align-items-center mb-2">
                      <div className="input-group input-group-sm" style={{ width: '150px' }}>
                        <Form.Control
                          type="number"
                          min={1}
                          value={tier.min_passengers}
                          onChange={(e) => updatePassengerTier(
                            index,
                            'min_passengers',
                            Number(e.target.value),
                          )}
                          placeholder="Min"
                        />
                        <span className="input-group-text">-</span>
                        <Form.Control
                          type="number"
                          min={0}
                          value={tier.max_passengers}
                          onChange={(e) => updatePassengerTier(
                            index,
                            'max_passengers',
                            Number(e.target.value),
                          )}
                          placeholder="Max"
                          title="0 = unlimited"
                        />
                      </div>
                      <span className="text-muted">pax</span>
                      <div className="input-group input-group-sm" style={{ width: '120px' }}>
                        <Form.Control
                          type="number"
                          min={0}
                          value={tier.price_eur}
                          onChange={(e) => updatePassengerTier(
                            index,
                            'price_eur',
                            Number(e.target.value),
                          )}
                        />
                        <span className="input-group-text">EUR</span>
                      </div>
                      <div className="input-group input-group-sm" style={{ width: '140px' }}>
                        <Form.Control
                          type="number"
                          min={0}
                          value={tier.price_huf || 0}
                          onChange={(e) => updatePassengerTier(
                            index,
                            'price_huf',
                            Number(e.target.value),
                          )}
                        />
                        <span className="input-group-text">HUF</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removePassengerTier(index)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  );
                })}
                <Form.Text className="text-muted">
                  Set max to 0 for unlimited (e.g., &quot;30+ passengers&quot;)
                </Form.Text>
              </Card.Body>
            </Card>
          )}

          {/* Vehicle Category pricing */}
          {experience.default_pricing?.pricing_type === 'vehicle_category' && (
            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fa fa-car me-2" />
                    Vehicle Category Prices
                  </span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value=""
                    onChange={(e) => {
                      if (e.target.value) addVehicleCategoryPrice(e.target.value);
                    }}
                    disabled={getAvailableCategories().length === 0}
                  >
                    <option value="">+ Add category...</option>
                    {getAvailableCategories().map((cat) => (
                      <option key={cat.Id} value={cat.Id}>
                        {cat.name}
                        {' '}
                        (
                        {cat.capacity}
                        {' '}
                        pax)
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Card.Header>
              <Card.Body className="p-2">
                {(!experience.default_pricing?.vehicle_category_prices
                  || experience.default_pricing.vehicle_category_prices.length === 0) && (
                  <div className="text-muted text-center py-3">
                    <i className="fa fa-info-circle me-2" />
                    No categories defined. Add vehicle categories with prices.
                  </div>
                )}
                {experience.default_pricing?.vehicle_category_prices?.map((catPrice, index) => {
                  const catKey = `cat-${catPrice.vehicle_category_id}`;
                  return (
                    <div key={catKey} className="d-flex gap-2 align-items-center mb-2">
                      <Badge bg="secondary" className="py-2" style={{ minWidth: '140px' }}>
                        {getCategoryName(catPrice.vehicle_category_id)}
                      </Badge>
                      <div className="input-group input-group-sm" style={{ width: '120px' }}>
                        <Form.Control
                          type="number"
                          min={0}
                          value={catPrice.price_eur}
                          onChange={(e) => updateVehicleCategoryPrice(
                            index,
                            'price_eur',
                            Number(e.target.value),
                          )}
                        />
                        <span className="input-group-text">EUR</span>
                      </div>
                      <div className="input-group input-group-sm" style={{ width: '140px' }}>
                        <Form.Control
                          type="number"
                          min={0}
                          value={catPrice.price_huf || 0}
                          onChange={(e) => updateVehicleCategoryPrice(
                            index,
                            'price_huf',
                            Number(e.target.value),
                          )}
                        />
                        <span className="input-group-text">HUF</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeVehicleCategoryPrice(index)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  );
                })}
                <Form.Text className="text-muted">
                  Set fixed prices per vehicle category for transfer-type experiences.
                </Form.Text>
              </Card.Body>
            </Card>
          )}

          {/* Starting & Ending Points */}
          <h6 className="border-bottom pb-2 mb-3 mt-4">
            <i className="fa fa-map-marker-alt me-2" />
            Starting & Ending Points
          </h6>

          <Alert variant="secondary" className="py-2">
            <i className="fa fa-info-circle me-2" />
            When this experience is selected for a booking,
            these will be copied to the destination from/to fields.
          </Alert>

          <div className="row">
            <Form.Group className="mb-3 col-6" controlId="starting_point">
              <Form.Label>Starting Point</Form.Label>
              <AddressAutocomplete
                value={experience.starting_point?.address || ''}
                onChange={(address) => setExperience((prev) => ({
                  ...prev,
                  starting_point: { ...prev.starting_point, address },
                }))}
                onCoordinatesChange={(coords) => setExperience((prev) => ({
                  ...prev,
                  starting_point: {
                    ...prev.starting_point,
                    latitude: coords.lat != null ? Number(coords.lat) : null,
                    longitude: coords.lon != null ? Number(coords.lon) : null,
                  },
                }))}
                placeholder="e.g., Budapest, Heroes' Square"
              />
              {experience.starting_point?.latitude && experience.starting_point?.longitude && (
                <small className="text-muted">
                  <i className="fa fa-map-marker-alt me-1" />
                  {Number(experience.starting_point.latitude).toFixed(4)}
                  ,
                  {' '}
                  {Number(experience.starting_point.longitude).toFixed(4)}
                </small>
              )}
              <FormControlFeedback errors={validationErrors} field="starting_point.address" />
            </Form.Group>

            <Form.Group className="mb-3 col-6" controlId="ending_point">
              <Form.Label>Ending Point</Form.Label>
              <AddressAutocomplete
                value={experience.ending_point?.address || ''}
                onChange={(address) => setExperience((prev) => ({
                  ...prev,
                  ending_point: { ...prev.ending_point, address },
                }))}
                onCoordinatesChange={(coords) => setExperience((prev) => ({
                  ...prev,
                  ending_point: {
                    ...prev.ending_point,
                    latitude: coords.lat != null ? Number(coords.lat) : null,
                    longitude: coords.lon != null ? Number(coords.lon) : null,
                  },
                }))}
                placeholder="e.g., Budapest, Fisherman's Bastion"
              />
              {experience.ending_point?.latitude && experience.ending_point?.longitude && (
                <small className="text-muted">
                  <i className="fa fa-map-marker-alt me-1" />
                  {Number(experience.ending_point.latitude).toFixed(4)}
                  ,
                  {' '}
                  {Number(experience.ending_point.longitude).toFixed(4)}
                </small>
              )}
              <FormControlFeedback errors={validationErrors} field="ending_point.address" />
            </Form.Group>
          </div>

          {/* Itinerary Template */}
          <h6 className="border-bottom pb-2 mb-3 mt-4">
            <i className="fa fa-list-ol me-2" />
            Itinerary Template
            <Badge bg="secondary" className="ms-2">
              {experience.itinerary_template?.length || 0}
            </Badge>
          </h6>

          <Alert variant="secondary" className="py-2">
            <i className="fa fa-info-circle me-2" />
            Define the typical activities for this experience.
            When a booking uses this template, these will be copied as a starting point.
          </Alert>

          {(!experience.itinerary_template || experience.itinerary_template.length === 0) && (
            <div className="text-muted text-center py-3 border rounded mb-3">
              <i className="fa fa-route me-2" />
              No activities defined yet.
            </div>
          )}

          {experience.itinerary_template?.map((activity, index) => {
            const activityKey = `activity-${activity.order || index}`;
            return (
              <Card key={activityKey} className="mb-2">
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="secondary" className="rounded-circle">{index + 1}</Badge>
                      <Form.Select
                        size="sm"
                        value={activity.stop_type || 'activity'}
                        onChange={(e) => updateItineraryActivity(index, 'stop_type', e.target.value)}
                        style={{ width: 'auto' }}
                      >
                        {STOP_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </Form.Select>
                    </div>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeItineraryActivity(index)}
                    >
                      <i className="fa fa-trash" />
                    </Button>
                  </div>
                  <div className="row g-2">
                    <div className="col-8">
                      <AddressAutocomplete
                        value={activity.address || ''}
                        onChange={(address) => {
                          // Only update address here; coordinates come from onCoordinatesChange
                          setExperience((prev) => {
                            const activities = [...(prev.itinerary_template || [])];
                            activities[index] = {
                              ...activities[index],
                              address,
                            };
                            return { ...prev, itinerary_template: activities };
                          });
                        }}
                        onCoordinatesChange={(coords) => {
                          setExperience((prev) => {
                            const activities = [...(prev.itinerary_template || [])];
                            activities[index] = {
                              ...activities[index],
                              latitude: coords.lat != null ? Number(coords.lat) : null,
                              longitude: coords.lon != null ? Number(coords.lon) : null,
                            };
                            return { ...prev, itinerary_template: activities };
                          });
                        }}
                        placeholder="Address (optional)"
                        className="form-control-sm"
                      />
                      {activity.latitude && activity.longitude && (
                        <small className="text-muted">
                          <i className="fa fa-map-marker-alt me-1" />
                          {Number(activity.latitude).toFixed(4)}
                          ,
                          {' '}
                          {Number(activity.longitude).toFixed(4)}
                        </small>
                      )}
                    </div>
                    <div className="col-4">
                      <div className="input-group input-group-sm">
                        <Form.Control
                          type="number"
                          min={0}
                          value={activity.duration_minutes || 0}
                          onChange={(e) => updateItineraryActivity(
                            index,
                            'duration_minutes',
                            Number(e.target.value),
                          )}
                        />
                        <span className="input-group-text">min</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={1}
                        placeholder="Description (optional)"
                        value={activity.description || ''}
                        onChange={(e) => updateItineraryActivity(
                          index,
                          'description',
                          e.target.value,
                        )}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            );
          })}

          <Button
            size="sm"
            variant="outline-primary"
            onClick={addItineraryActivity}
            className="mb-3"
          >
            <i className="fa fa-plus me-1" />
            Add Activity
          </Button>

          {/* Website Display */}
          <h6 className="border-bottom pb-2 mb-3 mt-4">
            <i className="fa fa-globe me-2" />
            Website Display
          </h6>

          <Alert variant="secondary" className="py-2">
            <i className="fa fa-info-circle me-2" />
            Configure how this experience appears on the public website.
          </Alert>

          <div className="row">
            <Form.Group className="mb-3 col-6" controlId="group_id">
              <Form.Label>Experience Group</Form.Label>
              <Form.Select
                value={experience.group_id || ''}
                onChange={(e) => setExperience((prev) => ({ ...prev, group_id: e.target.value }))}
              >
                <option value="">No group</option>
                {experienceGroups.map((group) => (
                  <option key={group.Id} value={group.Id}>{group.title}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Assign to a group to display on the website
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3 col-3" controlId="sort_order">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={experience.sort_order || 0}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Order within group
              </Form.Text>
            </Form.Group>
          </div>

          <Form.Group className="mb-3" controlId="link">
            <Form.Label>URL Path (optional)</Form.Label>
            <Form.Control
              type="text"
              value={experience.link || ''}
              onChange={handleChange}
              placeholder="/destinations/budapest-to-vienna"
            />
            <Form.Text className="text-muted">
              URL path for the experience page
            </Form.Text>
          </Form.Group>

          {/* Public Booking */}
          <h6 className="border-bottom pb-2 mb-3 mt-4">
            <i className="fa fa-shopping-cart me-2" />
            Public Booking
          </h6>

          <Alert variant="secondary" className="py-2 mb-3">
            <i className="fa fa-info-circle me-2" />
            Configure online booking for the wine tours website.
            Leave empty to hide the booking panel.
          </Alert>

          <div className="row">
            <Form.Group className="mb-3 col-4" controlId="max_passengers">
              <Form.Label>Max Passengers</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={50}
                value={experience.max_passengers || 8}
                onChange={(e) => setExperience((prev) => ({
                  ...prev,
                  max_passengers: Number(e.target.value),
                }))}
              />
              <Form.Text className="text-muted">Max group size for online booking</Form.Text>
            </Form.Group>
          </div>

          {/* Blocked Dates */}
          <Form.Group className="mb-3">
            <Form.Label>Blocked Dates</Form.Label>
            <div className="d-flex gap-2 mb-2">
              <Form.Control
                type="date"
                value={newBlockedDate}
                onChange={(e) => setNewBlockedDate(e.target.value)}
                style={{ maxWidth: '200px' }}
              />
              <Button variant="outline-secondary" size="sm" onClick={addBlockedDate}>
                + Add Date
              </Button>
            </div>
            {(experience.blocked_dates || []).length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {(experience.blocked_dates || []).map((date) => (
                  <Badge key={date} bg="secondary" className="d-flex align-items-center gap-1 py-2 px-2">
                    {date}
                    <button
                      type="button"
                      onClick={() => removeBlockedDate(date)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        padding: '0 0 0 4px',
                        cursor: 'pointer',
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <Form.Text className="text-muted">No blocked dates — all dates are available for booking.</Form.Text>
            )}
          </Form.Group>

          {/* Add-ons */}
          <Form.Group className="mb-3">
            <Form.Label>Add-ons</Form.Label>
            {(experience.add_ons || []).map((addon, index) => (
              <Card key={addon.id || index} className="mb-2">
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <small className="text-muted fw-semibold">
                      Add-on #
                      {index + 1}
                    </small>
                    <Button variant="outline-danger" size="sm" onClick={() => removeAddOn(index)}>
                      <i className="fa fa-trash" />
                    </Button>
                  </div>
                  <div className="row g-2">
                    <Form.Group className="col-6">
                      <Form.Label className="small mb-1">Name *</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={addon.name}
                        onChange={(e) => updateAddOn(index, 'name', e.target.value)}
                        placeholder="e.g. Sommelier Upgrade"
                      />
                    </Form.Group>
                    <Form.Group className="col-6">
                      <Form.Label className="small mb-1">Price Type</Form.Label>
                      <Form.Select
                        size="sm"
                        value={addon.price_type}
                        onChange={(e) => updateAddOn(index, 'price_type', e.target.value)}
                      >
                        <option value="per_person">Per Person</option>
                        <option value="flat">Flat Fee</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="col-3">
                      <Form.Label className="small mb-1">Price EUR</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={0}
                        value={addon.price_eur}
                        onChange={(e) => updateAddOn(index, 'price_eur', Number(e.target.value))}
                      />
                    </Form.Group>
                    <Form.Group className="col-3">
                      <Form.Label className="small mb-1">Price HUF</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={0}
                        value={addon.price_huf || 0}
                        onChange={(e) => updateAddOn(index, 'price_huf', Number(e.target.value))}
                      />
                    </Form.Group>
                    <Form.Group className="col-6">
                      <Form.Label className="small mb-1">Description</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={addon.description || ''}
                        onChange={(e) => updateAddOn(index, 'description', e.target.value)}
                        placeholder="Short description shown to guest"
                      />
                    </Form.Group>
                  </div>
                </Card.Body>
              </Card>
            ))}
            <Button variant="outline-primary" size="sm" onClick={addAddOn}>
              <i className="fa fa-plus me-1" />
              Add Add-on
            </Button>
          </Form.Group>

          {/* Image Upload */}
          <Form.Group className="mb-3">
            <Form.Label>Experience Image</Form.Label>
            <div className="d-flex align-items-start gap-3">
              {(experience.image_url || experience.imagePreview) && (
                <div className="position-relative">
                  <Image
                    src={experience.image_url || experience.imagePreview}
                    alt="Experience"
                    thumbnail
                    style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'cover' }}
                  />
                  <Button
                    size="sm"
                    variant="danger"
                    className="position-absolute top-0 end-0"
                    onClick={handleRemoveImage}
                    disabled={imageUploading}
                    style={{ transform: 'translate(50%, -50%)' }}
                  >
                    <i className="fa fa-times" />
                  </Button>
                </div>
              )}
              <div>
                <Form.Control
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleImageSelect}
                  ref={fileInputRef}
                  disabled={imageUploading}
                />
                <Form.Text className="text-muted">
                  PNG, JPEG, or WebP. Max 5MB.
                </Form.Text>
                {imageUploading && (
                  <div className="text-primary mt-1">
                    <i className="fa fa-spin fa-spinner me-1" />
                    Uploading...
                  </div>
                )}
              </div>
            </div>
          </Form.Group>

          {/* Status */}
          <h6 className="border-bottom pb-2 mb-3 mt-4">
            <i className="fa fa-toggle-on me-2" />
            Status
          </h6>

          <Form.Group className="mb-3" controlId="active">
            <Form.Select value={experience.active} onChange={handleChange}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <i className="fa fa-spin fa-spinner me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="fa fa-save me-2" />
              Save Experience
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ExperienceInputModal;
